import {
	AppHeader,
	AppHeaderButton,
	type AppHeaderMenuItem,
	AppHeaderSegmented,
	AppShellFrame,
	buildProNavGroups,
	PRO_MODES,
	type ProModeId,
	ProPlantSelect,
	proDashboardSection,
	type ProSectionId,
	SidebarNavigation,
} from '@slims-pro/ui/organisms';
import { ProLogo } from '@slims-pro/ui/atoms';
import { Bell, FileText, Moon, Settings, Sun, User } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { ProfileModal } from '@/components/modals/profile-modal';
import { CLIENT } from '@/generated/client';
import { useMe } from '@/hooks/use-me';
import { useMeta } from '@/hooks/use-meta';
import { usePlants } from '@/hooks/use-plants';
import { roleLabel } from '@/lib/format';
import { RouterLink } from '@/lib/router-link';
import { toPlantOption } from '@/mappers/plants';
import { usePlantStore } from '@/stores/plant-store';
import { useUIStore } from '@/stores/ui-store';

const PRO_MODE_KEY = 'pro-mode';

function readMode(modes: readonly ProModeId[], fallback: ProModeId): ProModeId {
	const stored = localStorage.getItem(PRO_MODE_KEY);
	return modes.includes(stored as ProModeId) ? (stored as ProModeId) : fallback;
}

/**
 * Shell SLIMS PRO — wiring puro sopra il chrome (`AppHeader`, `SidebarNavigation`,
 * `AppShellFrame`): cambiano solo la tinta (`brand`) e il contenuto degli slot. Voci di
 * navigazione, modalità e pulsanti dell'header seguono i permessi di `/api/v1/me`.
 */
export function ProShell() {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const openModal = useUIStore((s) => s.openModal);
	const theme = useUIStore((s) => s.theme);
	const toggleTheme = useUIStore((s) => s.toggleTheme);
	// Both loaded by ProtectedRoute before the shell renders.
	const meta = useMeta().data!;
	const me = useMe().data!;

	const enabled = useMemo(
		() => new Set<ProSectionId>(me.permissions.map((p) => p.module)),
		[me.permissions],
	);
	const modes = PRO_MODES.filter((m) => enabled.has(m.id));
	const modeIds = modes.map((m) => m.id);
	const fallbackMode = modeIds.includes(meta.defaultMode) ? meta.defaultMode : modeIds[0];

	const [mode, setMode] = useState<ProModeId>(() => readMode(modeIds, fallbackMode ?? 'fleet'));

	const section = (pathname.split('/')[1] || proDashboardSection(mode)) as ProSectionId;
	const navGroups = buildProNavGroups(mode, 'Dashboard', {}, enabled);

	const kind = mode === 'single' ? 'ci' : mode === 'field' ? 'utility' : undefined;
	const plants = usePlants(kind ? { kind } : {});
	const plantOptions = kind ? (plants.data?.items ?? []).map(toPlantOption) : [];
	const selectedPlantId = usePlantStore((s) =>
		kind ? s.selected[mode as 'single' | 'field'] : null,
	);
	const selectPlant = usePlantStore((s) => s.select);

	const goTo = useCallback((id: string) => navigate(`/${id}`), [navigate]);

	const handleModeChange = useCallback(
		(next: string) => {
			localStorage.setItem(PRO_MODE_KEY, next);
			setMode(next as ProModeId);
			navigate(`/${proDashboardSection(next as ProModeId)}`);
		},
		[navigate],
	);

	const menuItems = useMemo<AppHeaderMenuItem[]>(
		() => [
			{
				id: 'profile',
				label: 'Il mio profilo',
				icon: <User />,
				onClick: () => openModal('profile'),
			},
		],
		[openModal],
	);

	return (
		<AppShellFrame
			header={
				<AppHeader
					accent="brand"
					logoSlot={
						<ProLogo
							theme={theme}
							href="/"
							linkComponent={RouterLink}
							src={theme === 'dark' ? (CLIENT.logo.srcDark ?? CLIENT.logo.src) : CLIENT.logo.src}
							variant={CLIENT.logo.mode}
							alt={CLIENT.appName}
						/>
					}
					leadingSlot={
						<>
							{modes.length > 1 && (
								<AppHeaderSegmented
									aria-label="Modalità"
									accent="brand"
									options={modes}
									activeId={mode}
									onChange={handleModeChange}
								/>
							)}
							<ProPlantSelect
								plants={plantOptions}
								selectedPlantId={selectedPlantId}
								onSelectPlant={(id) => kind && selectPlant(mode as 'single' | 'field', id)}
							/>
						</>
					}
					actionsSlot={
						<>
							{enabled.has('reports') && (
								<AppHeaderButton
									icon={<FileText />}
									label="Report Tecnici"
									tone="green"
									onClick={() => goTo('reports')}
								/>
							)}
							{enabled.has('config') && (
								<AppHeaderButton
									icon={<Settings />}
									label="Configurazione"
									tone="purple"
									onClick={() => goTo('config')}
								/>
							)}
							<AppHeaderButton
								icon={theme === 'light' ? <Moon /> : <Sun />}
								title={theme === 'light' ? 'Passa al tema scuro' : 'Passa al tema chiaro'}
								aria-label="Cambia tema"
								tone="brand"
								onClick={toggleTheme}
							/>
						</>
					}
					notificationsSlot={
						enabled.has('anomaly') ? (
							<AppHeaderButton
								icon={<Bell />}
								title="Anomalie"
								aria-label="Anomalie"
								tone="brand"
								onClick={() => goTo('anomaly')}
							/>
						) : null
					}
					fullName={me.fullName ?? null}
					email={me.email ?? null}
					roleLabel={roleLabel(me.roles)}
					menuItems={menuItems}
				/>
			}
			sidebar={
				<SidebarNavigation accent="brand" items={navGroups} activeId={section} onNavigate={goTo} />
			}
			overlays={<ProfileModal />}
		>
			<Outlet />
		</AppShellFrame>
	);
}
